(() => {
  const BASE_URL = "https://media.salomon.hanasake.jp/".replace(/\/$/, "");
  const REST_ROOT = `${BASE_URL}/wp-json/wp/v2`;

  const parseSingleOrPluralValue = value => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(",") : undefined;
    } else if (typeof value === "string") {
      return value.length > 0 ? value : undefined;
    } else {
      return value ?? undefined;
    }
  };

  const convertLinkForShopify = wpLink => wpLink.replace(`${BASE_URL}/archives`, "/a/archives");

  const parseNoHyphensISO8601Date = noHyphensDate => {
    const [, year, month, date] = /^(\d{4})(\d{2})(\d{2})?$/.exec(noHyphensDate);
    return new Date(`${year}-${month}-${date}`);
  };

  const formatISO8601Duration = iso8601Duration => {
    const [, ...matches] = /^PT(\d+H)?(\d+M)?(\d+S)?$/.exec(iso8601Duration);
    const components = matches.map(match => match ? /^(\d+)([HMS])$/.exec(match).slice(1) : []);
    const [hours] = components.find(([, unit]) => unit === "H") ?? [];
    const [minutes] = components.find(([, unit]) => unit === "M") ?? ["0"];
    const [seconds] = components.find(([, unit]) => unit === "S") ?? ["0"];
    const displayedComponents = typeof hours === "string" ? [hours, minutes, seconds] : [minutes, seconds];
    return displayedComponents
      .map(component => component.padStart(2, "0"))
      .join(":");
  }

  const findTaxonomies = async (slug) => {
    const url = new URL(`${REST_ROOT}/${slug}`);
    const fields = ["id", "name", "slug"];

    url.searchParams.append("context", "embed");
    url.searchParams.append("_fields", fields.join(","));

    // Should convert URL to string before fetching,
    // because `wiser_recom.js` will intercept fetch requests, and it will fail if the URL is not a string.
    const res = await fetch(url.toString());
    const taxonomies = await res.json();
    return taxonomies.map(taxonomy => ({
      ...taxonomy,
      slug: decodeURIComponent(taxonomy.slug)
    }));
  };

  const findPosts = async (
    postType,
    {
      fields,
      query,
      page = 1,
      perPage = 10,
      order = "desc",
      orderBy = "date",
      mapPost
    } = {}
  ) => {
    const url = new URL(`${REST_ROOT}/${postType}`);

    url.searchParams.append("context", "embed");
    url.searchParams.append("page", String(page));
    url.searchParams.append("per_page", String(perPage));
    url.searchParams.append("order", order);
    url.searchParams.append("orderby", orderBy);

    if (Array.isArray(fields)) {
      url.searchParams.append("_fields", fields.join(","));
    }
    if (query) {
      for (const [name, value] of Object.entries(query)) {
        if (value !== null && value !== undefined) {
          url.searchParams.append(name, String(value));
        }
      }
    }

    // Should convert URL to string before fetching,
    // because `wiser_recom.js` will intercept fetch requests, and it will fail if the URL is not a string.
    const res = await fetch(url.toString());
    const posts = await res.json();
    const total = res.headers.get("X-WP-Total");
    const totalPages = res.headers.get("X-WP-TotalPages");

    return {
      pageInfo: {
        page,
        perPage,
        total: total ? Number.parseInt(total) : undefined,
        totalPages: totalPages ? Number.parseInt(totalPages) : undefined
      },
      items: posts.map(post => mapPost?.(post) ?? {})
    };
  }

  window.WordPress = {
    api: {
      storyAndGuideCategories: () => findTaxonomies("story-guide-categories"),
      storyAndGuideTags: () => findTaxonomies("story-guide-tags"),
      storiesAndGuides: ({ contentType, categories, tags, ...options }) => findPosts("stories-guides", {
        ...options,
        fields: [
          "id",
          "link",
          "title",
          "category_terms",
          "tag_terms",
          "plain_text_excerpt",
          "featured_media_metadata",
          "youtube_metadata",
          "acf"
        ],
        query: {
          content_type: contentType,
          categories: parseSingleOrPluralValue(categories),
          tags: parseSingleOrPluralValue(tags)
        },
        mapPost: post => ({
          id: post.id,
          type: post.acf.content_type,
          title: post.title.rendered,
          excerpt: post.plain_text_excerpt,
          link: post.acf.content_type === "another_page"
            ? post.acf.page_url
            : convertLinkForShopify(post.link),
          categories: post.category_terms,
          tags: post.tag_terms,
          featuredMediaUrl: post.featured_media_metadata?.post_thumbnail_url,
          highlighted: post.acf.highlighted,
          youtubeMetadata: post.youtube_metadata && {
            id: post.youtube_metadata.id,
            url: post.youtube_metadata.url,
            title: post.youtube_metadata.title,
            duration: typeof post.youtube_metadata.duration === "string"
              ? formatISO8601Duration(post.youtube_metadata.duration)
              : undefined
          }
        })
      }),
      athletes: ({ categories, tags, nationalities, ...options }) => findPosts("athletes", {
        ...options,
        fields: [
          "id",
          "link",
          "tag_terms",
          "nationalities",
          "athlete_images",
          "acf"
        ],
        query: {
          categories: parseSingleOrPluralValue(categories),
          tags: parseSingleOrPluralValue(tags),
          nationalities: parseSingleOrPluralValue(nationalities)
        },
        mapPost: post => ({
          id: post.id,
          fullname: post.acf.fullname,
          link: convertLinkForShopify(post.link),
          tags: post.tag_terms,
          nationality: Array.isArray(post.nationalities) && post.nationalities.length > 0
            ? post.nationalities[0]
            : undefined,
          featuredMedia: {
            url: post.athlete_images.profile.url,
            darken: post.athlete_images.profile.darken
          }
        })
      }),
      columnCategories: () => findTaxonomies("column-categories"),
      columnTags: () => findTaxonomies("column-tags"),
      columns: ({ contentType, categories, tags, ...options }) => findPosts("columns", {
        ...options,
        fields: [
          "id",
          "link",
          "title",
          "category_terms",
          "tag_terms",
          "plain_text_excerpt",
          "featured_media_metadata",
          "youtube_metadata",
          "acf"
        ],
        query: {
          content_type: contentType,
          categories: parseSingleOrPluralValue(categories),
          tags: parseSingleOrPluralValue(tags)
        },
        mapPost: post => ({
          id: post.id,
          type: post.acf.content_type,
          title: post.title.rendered,
          excerpt: post.plain_text_excerpt,
          link: post.acf.content_type === "another_page"
            ? post.acf.page_url
            : convertLinkForShopify(post.link),
          categories: post.category_terms,
          tags: post.tag_terms,
          featuredMediaUrl: post.featured_media_metadata?.post_thumbnail_url,
          highlighted: post.acf.highlighted,
          youtubeMetadata: post.youtube_metadata && {
            id: post.youtube_metadata.id,
            url: post.youtube_metadata.url,
            title: post.youtube_metadata.title,
            duration: typeof post.youtube_metadata.duration === "string"
              ? formatISO8601Duration(post.youtube_metadata.duration)
              : undefined
          }
        })
      }),
      eventCategories: () => findTaxonomies("event-categories"),
      eventTags: () => findTaxonomies("event-tags"),
      events: ({ eventStatuses, levels, categories, tags, ...options }) => findPosts("events", {
        ...options,
        fields: [
          "id",
          "link",
          "title",
          "category_terms",
          "tag_terms",
          "featured_media_metadata",
          "event_status",
          "level",
          "acf"
        ],
        query: {
          event_statuses: eventStatuses,
          levels,
          categories: parseSingleOrPluralValue(categories),
          tags: parseSingleOrPluralValue(tags)
        },
        mapPost: post => ({
          id: post.id,
          type: post.acf.content_type,
          title: post.title.rendered,
          excerpt: post.plain_text_excerpt,
          link: post.acf.content_type === "another_page"
            ? post.acf.page_url
            : convertLinkForShopify(post.link),
          categories: post.category_terms,
          tags: post.tag_terms,
          featuredMediaUrl: post.featured_media_metadata?.post_thumbnail_url,
          eventStatus: post.event_status?.label,
          level: post.level?.label,
          startDate: parseNoHyphensISO8601Date(post.acf.start_date),
          endDate: post.acf.end_date
            ? parseNoHyphensISO8601Date(post.acf.end_date)
            : undefined,
          eventTime: post.acf.event_time,
          venueName: post.acf.venue_name
        })
      }),
      eventReportCategories: () => findTaxonomies("event-report-categories"),
      eventReportTags: () => findTaxonomies("event-report-tags"),
      eventReports: ({ contentType, categories, tags, ...options }) => findPosts("event-reports", {
        ...options,
        fields: [
          "id",
          "link",
          "title",
          "category_terms",
          "tag_terms",
          "plain_text_excerpt",
          "featured_media_metadata",
          "youtube_metadata",
          "acf"
        ],
        query: {
          content_type: contentType,
          categories: parseSingleOrPluralValue(categories),
          tags: parseSingleOrPluralValue(tags)
        },
        mapPost: post => ({
          id: post.id,
          type: post.acf.content_type,
          title: post.title.rendered,
          excerpt: post.plain_text_excerpt,
          link: post.acf.content_type === "another_page"
              ? post.acf.page_url
              : convertLinkForShopify(post.link),
          categories: post.category_terms,
          tags: post.tag_terms,
          featuredMediaUrl: post.featured_media_metadata?.post_thumbnail_url,
          highlighted: post.acf.highlighted,
          youtubeMetadata: post.youtube_metadata && {
            id: post.youtube_metadata.id,
            url: post.youtube_metadata.url,
            title: post.youtube_metadata.title,
            duration: typeof post.youtube_metadata.duration === "string"
                ? formatISO8601Duration(post.youtube_metadata.duration)
                : undefined
          }
        })
      }),
    },
    utils: {
      convertLinkForShopify
    }
  };
})();
